// UIFIX1 (docs/build-briefs/UIFIX1-reports-tables-controls.md; design/notes/console-design-fix-plan.md SF-1): the one report toolbar
// pieces every Reports result shares in all three consoles. A filter pill is ONE line ("Dates: Last 30 days", never wrapped) and
// the Export menu is ONE button that opens CSV, PDF, Google Sheets and Schedule email. A state the person cannot use (Google
// Sheets not set up, a report that cannot be scheduled) shows inside the menu with its reason, never as a permanent disabled
// button on the toolbar. The hosts keep every request and every permission; this file only draws.

export interface ToolbarItem {
  testid: string;
  label: string;
  disabled: boolean;
  /** A plain sentence shown under a disabled item, so a blocked item always says why. */
  reason: string;
  run: () => void;
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

/** A filter pill: "Dates: Last 30 days" on one line. `popover` is anchored under it by the caller's wrapper. */
export function filterPill(label: string, value: string, testid: string, isOpen: boolean, action: () => void): HTMLButtonElement {
  const element = node("button", `rx-sel${isOpen ? " open" : ""}`);
  element.type = "button";
  element.dataset["testid"] = testid;
  element.setAttribute("aria-haspopup", "dialog");
  element.setAttribute("aria-expanded", String(isOpen));
  element.append(node("span", "rx-sel-label", `${label}:`), node("span", "rx-sel-value", value), node("span", "rx-sel-caret", "▾"));
  element.addEventListener("click", action);
  return element;
}

/** The wrapper that anchors a popover under its pill. */
export function pillWrap(...children: Node[]): HTMLElement {
  const wrap = node("span", "rx-pill-wrap");
  wrap.append(...children);
  return wrap;
}

/** The single Export menu. `testid` prefixes the trigger (`${testid}-menu`); each item keeps its own test id. */
export function exportMenu(options: { testid: string; open: boolean; toggle: () => void; close: () => void; items: readonly ToolbarItem[]; disabled: boolean }): HTMLElement {
  const wrap = node("span", "rx-menu-wrap");
  wrap.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && options.open) options.close();
  });
  const trigger = node("button", "rx-btn rx-export-trigger");
  trigger.type = "button";
  trigger.dataset["testid"] = `${options.testid}-export-menu`;
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", String(options.open));
  trigger.disabled = options.disabled;
  trigger.append(node("span", undefined, "Export"), node("span", "rx-sel-caret", "▾"));
  trigger.addEventListener("click", options.toggle);
  wrap.append(trigger);
  if (options.open) {
    const menu = node("div", "rx-menu");
    menu.setAttribute("role", "menu");
    menu.dataset["testid"] = `${options.testid}-export-list`;
    for (const item of options.items) {
      const entry = node("button", "rx-menu-item");
      entry.type = "button";
      entry.setAttribute("role", "menuitem");
      entry.dataset["testid"] = item.testid;
      entry.disabled = item.disabled;
      entry.append(node("span", "rx-menu-label", item.label));
      if (item.disabled && item.reason !== "") entry.append(node("small", "rx-menu-reason", item.reason));
      entry.addEventListener("click", () => {
        options.close();
        item.run();
      });
      menu.append(entry);
    }
    wrap.append(menu);
  }
  return wrap;
}
